<script setup lang="ts">
import { Ban } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import { cancelJob, isRunning, jobPercent, useJob } from "@/api/jobs";
import DescriptionList, { type DescriptionItem } from "@/components/common/DescriptionList.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import JsonView from "@/components/common/JsonView.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import ResultView from "@/components/common/ResultView.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { confirm } from "@/composables/useConfirm";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { formatDateTime, formatDuration } from "@/lib/format";

definePage({ meta: { title: "Job" } });

const route = useRoute("/jobs/[id]");
const queryClient = useQueryClient();
const job = useJob(() => route.params.id);
const data = computed(() => job.data.value);
const running = computed(() => (data.value ? isRunning(data.value) : false));
const percent = computed(() => (data.value ? jobPercent(data.value) : null));
const duration = computed(() => (data.value ? new Date(data.value.finishedAt ?? Date.now()).getTime() - new Date(data.value.createdAt).getTime() : null));
const facts = computed<DescriptionItem[]>(() => {
  const d = data.value;
  if (!d) return [];
  return [
    { label: "Kind", value: d.kind, mono: true },
    { label: "Started", key: "started" },
    { label: "Duration", value: formatDuration(duration.value) },
    ...(d.mcpTask ? [{ label: "Source", value: "Claude task" }] : []),
  ];
});
const cancelling = ref(false);

async function cancel() {
  if (!data.value) return;
  const answer = await confirm({ title: t.jobs.cancel, description: t.jobs.cancelConfirm, confirmLabel: t.jobs.cancel, destructive: true });
  if (!answer) return;
  cancelling.value = true;
  try {
    await cancelJob(data.value.id);
    toast.success(t.jobs.cancelled);
    await Promise.all([job.refetch(), queryClient.invalidateQueries({ queryKey: ["jobs"] })]);
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    cancelling.value = false;
  }
}
</script>

<template>
  <div>
    <ListSkeleton v-if="job.isPending.value" :rows="4" />
    <ErrorState v-else-if="job.error.value" :error="job.error.value" what="Job" @retry="job.refetch()" />
    <template v-else-if="data">
      <PageHeader :title="data.name" :crumbs="[{ label: t.jobs.title, to: '/jobs' }, { label: data.name }]">
        <template #badges><StatusBadge :status="data.status" /></template>
        <template #meta>
          <DescriptionList :items="facts" layout="inline" dense class="mt-2">
            <template #started><span :title="formatDateTime(data.createdAt)"><RelativeTime :value="data.createdAt" /></span></template>
          </DescriptionList>
        </template>
        <template v-if="running" #actions>
          <Button variant="outline" :disabled="cancelling" @click="cancel"><Ban /> {{ t.jobs.cancel }}</Button>
        </template>
      </PageHeader>

      <div class="flex flex-col gap-6">
        <div v-if="running" class="surface flex flex-col gap-2 p-4" aria-live="polite">
          <Progress :model-value="percent ?? undefined" class="h-2" :class="percent === null && 'animate-pulse'" :aria-label="percent === null ? 'In progress' : `${percent}%`" />
          <p class="text-sm [overflow-wrap:anywhere] text-muted-foreground">{{ data.statusMessage ?? "Working…" }}<template v-if="percent !== null"> · {{ percent }}%</template></p>
        </div>
        <p v-else-if="data.statusMessage" class="text-sm [overflow-wrap:anywhere] text-muted-foreground">{{ data.statusMessage }}</p>

        <section v-if="data.error" class="flex flex-col gap-2">
          <h2 class="section-title">{{ t.jobs.error }}</h2>
          <pre class="overflow-x-auto rounded-md border border-destructive/30 bg-destructive-soft p-3 text-sm whitespace-pre-wrap text-destructive">{{ data.error }}</pre>
        </section>
        <section v-if="data.result !== null && data.result !== undefined" class="flex flex-col gap-2">
          <h2 class="section-title">{{ t.jobs.result }}</h2>
          <ResultView :value="data.result" />
        </section>
        <section class="flex flex-col gap-2">
          <h2 class="section-title">{{ t.jobs.input }}</h2>
          <JsonView :value="data.input" label="input" />
        </section>
      </div>
    </template>
  </div>
</template>
